<template>
  <div>
    <base-header class="pb-6"></base-header>
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header border-0">
          <h3 class="mb-0">Lista de Precantidades Foodcourt</h3>
        </div>

        <div v-if="loading" class="text-center text-muted py-5">Cargando forecast…</div>
        <div v-else-if="error" class="text-center text-danger py-5">
          No se pudo cargar el forecast. {{ error }}
        </div>

        <template v-else-if="data && selectedKitchens.length">
          <!-- Cocina: solo el rol interno MV elige. El franquiciado ve la suya y punto. -->
          <div v-if="isInternal" class="card-header border-0 pt-0">
            <label class="form-control-label">Cocina</label>
            <el-select v-model="kid" class="w-100 w-md-50" filterable>
              <el-option :value="null" label="Consolidado · todas las cocinas"></el-option>
              <el-option
                v-for="k in kitchensSorted"
                :key="k.id"
                :label="k.name + ' · ' + k.country"
                :value="k.id"
              ></el-option>
            </el-select>
          </div>

          <!-- Canal de venta: manda sobre lo que se ve Y sobre lo que se descarga. -->
          <div class="card-header border-0 pt-0">
            <label class="form-control-label">Canal de venta</label>
            <div class="pill-row">
              <button
                v-for="ch in data.channels"
                :key="ch"
                class="pill"
                :class="{ on: canal === ch }"
                @click="canal = ch"
              >{{ chLabel(ch) }}</button>
            </div>
          </div>

          <div class="row starter-page">
            <div class="container-fluid">
              <el-table
                class="table-responsive table-flush"
                header-row-class-name="thead-light"
                :data="dias"
                empty-text="Sin días para proyectar."
              >
                <el-table-column label="Día" min-width="190px">
                  <template v-slot="{row}">
                    <div class="d-flex align-items-center">
                      <span>{{ row.label }}</span>
                      <span
                        v-if="row.feriado"
                        class="flag ml-2"
                        :title="'Feriado: ' + row.feriado"
                      >⚑</span>
                    </div>
                    <div v-if="row.feriado" class="text-xs hol-txt">{{ row.feriado }}</div>
                  </template>
                </el-table-column>

                <el-table-column label="Cocina" min-width="200px">
                  <template>{{ kitchenLabel }}</template>
                </el-table-column>

                <el-table-column
                  :label="'Precantidad · ' + chLabel(canal)"
                  :render-header="encabezadoPrecantidad"
                  min-width="170px"
                  align="right"
                >
                  <template v-slot="{row}">
                    <span class="font-weight-bold text-success">
                      {{ fmt(precantidad(row.iso, canal)) }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column label="Turnos" min-width="190px">
                  <template v-slot="{row}">
                    <div v-if="shifts" class="text-xs">
                      <div>
                        <b>T1</b> {{ shifts.openingTime }}–{{ shifts.cutoffTime }} ·
                        {{ fmt(precantidad(row.iso, canal) * shifts.t1) }} platos
                      </div>
                      <div>
                        <b>T2</b> {{ shifts.cutoffTime }}–{{ shifts.closingTime }} ·
                        {{ fmt(precantidad(row.iso, canal) * shifts.t2) }} platos
                      </div>
                    </div>
                    <span v-else class="text-xs text-muted">—</span>
                  </template>
                </el-table-column>

                <el-table-column label="Reporte" min-width="170px">
                  <template v-slot="{row}">
                    <base-button
                      type="danger"
                      size="sm"
                      :loading="bajando === row.iso + ':pdf'"
                      @click="bajar(row.iso, 'pdf')"
                    >
                      <i class="far fa-file-pdf"></i> Descargar
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>

              <div class="px-3 pb-3">
                <div
                  v-for="(a, i) in avisos"
                  :key="'c' + i"
                  class="text-xs aviso mb-2"
                  :class="{ grave: a.nivel === 'incompleto' }"
                >
                  <b>{{ a.cocina }} — {{ a.titulo }}</b> {{ a.detalle }}
                </div>
                <div v-if="enCampana.length" class="text-xs aviso mb-2">
                  ⚠ Campaña o lanzamiento detectado en
                  <b>{{ enCampana.map(k => k.name).join(", ") }}</b>: la última semana
                  vendió más del doble que la mediana de las 4 previas. La proyección
                  histórica no puede anticipar una campaña — esa semana se planifica con
                  el calendario de Operaciones, no con este número.
                </div>
                <div class="text-xs text-muted mt-2">
                  <b>Proyección</b> = nivel de los últimos 7 días abiertos × perfil del día
                  de la semana (28 días) × share del plato (14 días). Los días cerrados y
                  los feriados salen de la historia; en feriado se aplica un factor
                  <b>aprendido</b> de los feriados pasados de la cocina
                  (×{{ fmt(chFactor(canal), 2) }} para {{ chLabel(canal) }}).
                  <b>Precantidad</b> = proyección × 1.05 redondeada hacia arriba
                  <b>por plato</b>: lo que se ve en la tabla es lo mismo que sale en las
                  descargas. Solo platos de la carta OnDemand.
                  <br />
                  El PDF agrupa por <b>topping</b> y en <b>kilos</b>, ordenado de mayor a
                  menor, con una tabla por turno: el corte entre turnos son las
                  {{ shifts ? shifts.cutoffTime : "17:00" }}, que es el valle del día. El
                  número de cada turno es lo que ese turno <b>consume</b>, no una
                  preparación única — la reposición va en tandas, como indica el manual de
                  mise en place. La lista de compra de insumos se pide en
                  <router-link to="/insumos/foodcourt">Insumos › Foodcourt</router-link>,
                  que trabaja por rango de días.
                </div>
              </div>
            </div>
          </div>

          <forecast-history :kitchen-id="historyKitchenId"></forecast-history>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import ForecastGateway from "@/gateways/ForecastGateway";
import ForecastHistory from "@/components/ForecastHistory";
import { Table, TableColumn, Select, Option, Tooltip } from "element-ui";

const CHLAB = { propio: "Propio", apps: "App", todos: "Todos" };
const BUFFER = 1.05;
const DIAS_A_PROYECTAR = 14;
const DOW_ES = [
  "Sábado",
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
];

export default {
  name: "lista-precantidades-foodcourt",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tooltip.name]: Tooltip,
    ForecastHistory,
  },
  data() {
    return {
      loading: true,
      error: null,
      data: null,
      kid: null,
      // Arranca en "todos" a proposito: es el canal con el que se cocina. Sumar
      // Propio y App por separado hace producir de mas (ver el tooltip de la columna).
      canal: "todos",
      bajando: null, // "<fecha>:<formato>" mientras el backend arma el archivo
    };
  },
  async mounted() {
    try {
      const res = await ForecastGateway.obtener();
      const p =
        (res.data && res.data.payload && res.data.payload.result) || res.data;
      this.data = p;
      if (p.kitchens && p.kitchens.length) {
        this.kid = p.kitchens
          .slice()
          .sort((a, b) => b.weekOrders - a.weekOrders)[0].id;
      }
    } catch (e) {
      // El backend responde 403 con motivo cuando el usuario no tiene cocina asignada.
      const api = e && e.response && e.response.data && e.response.data.payload;
      this.error = (api && api.message) || (e && e.message) || "error";
    } finally {
      this.loading = false;
    }
  },
  computed: {
    kitchensSorted() {
      return this.data
        ? this.data.kitchens.slice().sort((a, b) => b.weekOrders - a.weekOrders)
        : [];
    },
    isInternal() {
      return !!(this.data && this.data.scope && this.data.scope.internal);
    },
    historyKitchenId() {
      return this.kid === null || this.kid === "" ? 0 : this.kid;
    },
    // kid null = consolidado (solo alcanzable por el rol interno MV).
    selectedKitchens() {
      if (!this.data) return [];
      if (this.kid === null || this.kid === "") return this.kitchensSorted;
      return this.kitchensSorted.filter((k) => k.id === this.kid);
    },
    kitchenLabel() {
      const sel = this.selectedKitchens;
      return sel.length === 1 ? sel[0].name : `Consolidado · ${sel.length} cocinas`;
    },
    holidays() {
      return (this.data && this.data.holidays) || {};
    },
    /**
     * El factor de feriado ya no es fijo: el backend lo APRENDE por cocina de los
     * feriados pasados. En consolidado se pondera por el volumen de cada cocina — una
     * cocina de 20 unidades no puede pesar igual que una de 700 — y si ninguna tiene
     * feriados con data queda en 1.0, que es no ajustar.
     */
    factorFeriadoAprendido() {
      const out = {};
      ["propio", "apps", "todos"].forEach((ch) => {
        let num = 0;
        let den = 0;
        this.selectedKitchens.forEach((k) => {
          const f = k.holidayFactor && k.holidayFactor[ch];
          if (!f) return;
          const peso = this.chDishes(k, ch).reduce((s, d) => s + d.wk, 0);
          if (peso <= 0) return;
          num += f.factor * peso;
          den += peso;
        });
        out[ch] = den > 0 ? num / den : 1;
      });
      return out;
    },
    avisos() {
      return this.selectedKitchens
        .filter((k) => k.confianza && k.confianza.nivel !== "ok")
        .map((k) => ({ ...k.confianza, cocina: k.name }));
    },
    shifts() {
      const sel = this.selectedKitchens.filter((k) => k.shifts);
      if (!sel.length) return null;
      let num = 0;
      let den = 0;
      let cierre = null;
      sel.forEach((k) => {
        const peso =
          this.chDishes(k, this.canal).reduce((s, d) => s + d.wk, 0) || 1;
        num += k.shifts.t2 * peso;
        den += peso;
        cierre = cierre === null ? k.shifts.closingTime : (k.shifts.closingTime > cierre ? k.shifts.closingTime : cierre);
      });
      const t2 = den > 0 ? num / den : 0;
      return {
        t1: 1 - t2,
        t2,
        openingTime: sel[0].shifts.openingTime,
        cutoffTime: sel[0].shifts.cutoffTime,
        cierre,
      };
    },
    /** Cocinas con una campaña/lanzamiento detectado: la fórmula no las puede predecir. */
    enCampana() {
      return this.selectedKitchens.filter(
        (k) => k.campana && k.campana.detectada
      );
    },
    /** Dias a producir: arranca HOY y va hacia adelante, en ese orden. */
    dias() {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const out = [];
      for (let i = 0; i < DIAS_A_PROYECTAR; i++) {
        const d = new Date(hoy);
        d.setDate(hoy.getDate() + i);
        const iso = this.iso(d);
        out.push({
          iso,
          label: `${iso} · ${DOW_ES[this.dow(iso)]}`,
          feriado: this.holidays[iso] || null,
        });
      }
      return out;
    },
  },
  methods: {
    fmt(n, d = 0) {
      return Number(n).toLocaleString("es-PE", {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });
    },
    chLabel(ch) {
      return CHLAB[ch] || ch;
    },
    /**
     * Encabezado de la columna con ayuda. Se usa render-header y no el slot `header`
     * porque element-ui 2.4.11 todavia no lo soporta.
     *
     * La ayuda existe por una confusion real: Todos no es Propio + App. La precantidad
     * redondea hacia arriba PLATO POR PLATO, y los platos que se venden por los dos
     * canales se redondean una sola vez al juntarlos, no dos.
     */
    encabezadoPrecantidad(h, { column }) {
      return h("span", [
        column.label + " ",
        h(
          "el-tooltip",
          {
            props: {
              effect: "dark",
              placement: "top",
              content:
                "Todos no es Propio + App. La precantidad redondea hacia arriba plato por " +
                "plato: los platos que se venden por los dos canales se redondean una sola " +
                "vez al juntarlos, no dos. Para cocinar usá Todos; sumar los canales por " +
                "separado hace producir de más.",
            },
          },
          [
            h("i", {
              class: "fas fa-question-circle",
              // inline y no CSS scoped: render-header genera los nodos fuera del
              // scope del componente, asi que .help-ico no llegaria a aplicar.
              style: { color: "#8a988f", cursor: "help", marginLeft: "2px" },
            }),
          ]
        ),
      ]);
    },
    iso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    },
    /** dia de semana MV: 0=Sabado ... 6=Viernes */
    dow(iso) {
      const d = new Date(iso + "T00:00:00");
      return (d.getDay() + 1) % 7;
    },
    chDishes(k, ch) {
      if (!k) return [];
      return ch === "propio" ? k.dishes : (k.ch && k.ch[ch] && k.ch[ch].dishes) || [];
    },
    chFactor(ch) {
      return this.factorFeriadoAprendido[ch] || 1;
    },
    factorDia(iso, ch) {
      return this.holidays[iso] ? this.chFactor(ch) : 1;
    },
    /** Platos con su proyeccion y precantidad para una fecha y canal concretos. */
    filas(iso, ch) {
      const i = this.dow(iso);
      const f = this.factorDia(iso, ch);
      const map = {};
      this.selectedKitchens.forEach((k) => {
        this.chDishes(k, ch).forEach((d) => {
          const proj = (d.d[i] || 0) * f;
          if (proj <= 0) return;
          if (!map[d.name]) map[d.name] = { name: d.name, cat: d.cat, proj: 0 };
          map[d.name].proj += proj;
        });
      });
      const out = Object.keys(map).map((n) => {
        const r = map[n];
        return { ...r, prep: Math.ceil(r.proj * BUFFER) };
      });

      return out.sort((a, b) => b.prep - a.prep);
    },
    /** Precantidad total del dia para un canal. */
    precantidad(iso, ch) {
      return this.filas(iso, ch).reduce((s, r) => s + r.prep, 0);
    },
    // ---- descargas ----
    /**
     * Baja el archivo que genera el backend. El nombre sale del Content-Disposition,
     * asi no hay dos fuentes de verdad para como se llama el archivo.
     */
    async bajar(iso, formato) {
      this.bajando = iso + ":" + formato;
      try {
        const res = await ForecastGateway.descargar(
          iso,
          this.canal,
          formato,
          this.kid
        );
        const disp = res.headers && res.headers["content-disposition"];
        const m = disp && disp.match(/filename="?([^";]+)"?/i);
        const nombre = m ? m[1] : `precantidades-${iso}-${this.canal}.${formato}`;
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = nombre;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        alert("No se pudo generar el archivo. " + ((e && e.message) || ""));
      } finally {
        this.bajando = null;
      }
    },
  },
};
</script>
<style scoped>
.starter-page {
  min-height: calc(100vh - 380px);
}
.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pill {
  border: 1px solid #dfe6e1;
  background: #fff;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #4a5a51;
  cursor: pointer;
}
.pill.on {
  background: #5dbc85;
  border-color: #5dbc85;
  color: #fff;
}
.flag {
  color: #b98d00;
}
.hol-txt {
  color: #8a6d00;
}
.aviso {
  background: #fff6e0;
  border-left: 3px solid #d9a400;
  color: #6b5200;
  padding: 8px 10px;
  border-radius: 3px;
}
.aviso.grave {
  background: #fdeceb;
  border-left-color: #c0392b;
  color: #7d241a;
}
</style>
